import axios from "axios";
import SecureLogger from "../utils/secureLogger.js";
import { getAuthorizedCompanyContext } from "../utils/companyContext.js";
import { applyCompanyContextHeader } from "./apiClientCompanyContext.js";
import { showCompanySuspensionNotice } from "../utils/companySuspensionNotice.js";
import { isCanceledApiRequest } from "./apiRequestPolicy.js";
import { installAxiosGlobalLoading } from "../utils/globalLoading.js";
import {
  emitSubscriptionAccessUpdate,
  isSubscriptionRestrictionCode,
} from "../utils/subscriptionAccessEvents.js";

// Create an Axios instance
const apiClient = axios.create({
  // baseURL can be set here if you have a common base URL for all requests
  timeout: 10000, // 10 secondes de timeout par défaut
});

// Installé en premier : Axios exécutera ce suivi après les intercepteurs de
// préparation de la requête et avant les intercepteurs métier de réponse.
installAxiosGlobalLoading(apiClient);

// ============================================
// INTERCEPTEUR REQUEST (Ajout automatique du token)
// ============================================
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
      const companyContextId = getAuthorizedCompanyContext();
      // Une requête de gestion multi-entreprises peut demander explicitement
      // un autre contexte. Le serveur valide toujours cette entreprise.
      applyCompanyContextHeader(config, companyContextId);
      SecureLogger.debug("Axios Interceptor: Authentication token added to request headers");
    } else {
      // Log warning si pas de token pour une requête (sauf routes publiques)
      if (!config.url?.includes('/api/register') && !config.url?.includes('/api/login')) {
        SecureLogger.warn("⚠️ Token manquant pour requête:", config.url);
      }
    }
    return config;
  },
  (error) => {
    SecureLogger.authError("Request Configuration", error);
    return Promise.reject(error);
  }
);

// ============================================
// INTERCEPTEUR RESPONSE (Gestion automatique des erreurs d'authentification)
// ============================================
apiClient.interceptors.response.use(
  // Succès : laisser passer la réponse
  (response) => {
    return response;
  },

  // Erreur : gestion centralisée
  (error) => {
    // Une annulation est un nettoyage normal lors d'un changement de page ou de filtre.
    // La propager au composant sans la journaliser évite les faux positifs en console.
    if (isCanceledApiRequest(error)) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const errorData = error.response?.data;
    const errorCode = errorData?.code;
    const errorMessage = errorData?.message || error.message;
    const isTimeout = error.code === 'ECONNABORTED';
    const isNetworkError = !error.response || error.code === 'ERR_NETWORK';

    if (status === 403 && isSubscriptionRestrictionCode(errorCode)) {
      emitSubscriptionAccessUpdate(errorData);
      return Promise.reject(error);
    }

    if (status === 403 && errorCode === 'COMPANY_SUSPENDED') {
      showCompanySuspensionNotice(errorData);
      return Promise.reject(error);
    }

    if (isTimeout || isNetworkError) {
      SecureLogger.warn(isTimeout ? '[API Timeout]' : '[API Network Error]', {
        url: error.config?.url,
        method: error.config?.method,
        code: error.code || 'NETWORK_ERROR',
        message: errorMessage,
      });
      return Promise.reject(error);
    }

    // Log l'erreur pour debugging
    SecureLogger.error(`[API Error ${status}]`, {
      url: error.config?.url,
      method: error.config?.method,
      code: errorCode,
      message: errorMessage,
    });

    // ============================================
    // 🔒 GESTION ERREURS D'AUTHENTIFICATION (401)
    // ============================================
    if (status === 401) {
      // Toutes les erreurs 401 = session expirée
      // On utilise un code générique pour ne pas exposer la logique métier
      SecureLogger.info('🔐 Session expirée, déconnexion automatique');
      localStorage.removeItem("authToken");
      localStorage.removeItem("userData");
      window.location.href = '/login?session=expired';
      return Promise.reject(error);
    }

    // ============================================
    // 🚫 GESTION ERREURS D'AUTORISATION (403)
    // ============================================
    if (status === 403) {
      // Permissions insuffisantes
      if (errorCode === 'PERMISSION_DENIED') {
        SecureLogger.warn('🔒 Permissions insuffisantes:', errorData?.required);
        // L'erreur sera gérée par le composant (toast, notification)
        return Promise.reject(error);
      }

      // Compte entreprise ou utilisateur inactif
      if (errorCode === 'COMPANY_INACTIVE' || errorCode === 'ACCOUNT_INACTIVE') {
        SecureLogger.warn('🏢 Compte inactif');
        localStorage.removeItem("authToken");
        localStorage.removeItem("userData");
        window.location.href = '/login?reason=account_inactive';
        return Promise.reject(error);
      }

      // Compte d'entreprise désactivé
      if (errorCode === 'COMPANY_DISABLED') {
        SecureLogger.warn('🏢 Entreprise désactivée');
        localStorage.removeItem("authToken");
        localStorage.removeItem("userData");
        window.location.href = '/login?reason=company_disabled';
        return Promise.reject(error);
      }

      // Aucun magasin actif
      if (errorCode === 'NO_WAREHOUSE_ACCESS' || errorCode === 'NO_ACTIVE_WAREHOUSES_IN_SYSTEM') {
        SecureLogger.warn('🏪 Aucun établissement accessible');
        // L'erreur sera gérée par le composant
        return Promise.reject(error);
      }
    }

    // ============================================
    // 📡 AUTRES ERREURS HTTP
    // ============================================
    // Les erreurs 400, 404, 409, 500, etc. sont gérées par les composants
    // On les laisse passer pour permettre une gestion personnalisée

    // Rejeter l'erreur pour que les composants puissent la gérer
    return Promise.reject(error);
  }
);

export default apiClient;
